// Self-service account API client. Online-only; uses apiFetch (auth header + 401 silent-refresh
// + { data, error } envelope). Returns a small { data, error } result the page maps to messages.

import { apiFetch } from './api';

export interface AccountMe {
  email: string;
  full_name: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  preferred_name: string | null;
  mobile: string | null;
  pref_theme: string;
  pref_dark_accent: string;
  pref_font_size: string;
  pref_time_entry: string;
  notif_roster_sms: boolean;
  notif_roster_email: boolean;
  notif_quiet_start: string | null;
  notif_quiet_end: string | null;
  home_route: string | null;
  // read-only display context
  pilot_code: string | null;
  home_base_id: string | null;
  last_login_at: string | null;
  roles: string[];
}

// Editable non-email profile fields (any subset).
export type ProfilePatch = Partial<Pick<AccountMe,
  'full_name' | 'first_name' | 'last_name' | 'company' | 'preferred_name' | 'mobile' |
  'pref_theme' | 'pref_dark_accent' | 'pref_font_size' | 'pref_time_entry' |
  'notif_roster_sms' | 'notif_roster_email' | 'notif_quiet_start' | 'notif_quiet_end' |
  'home_route'>>;

type Result<T> = { data: T | null; error: string | null };
const unwrap = <T,>(r: { data: T | null; error: { message: string } | null }): Result<T> =>
  r.error ? { data: null, error: r.error.message } : { data: r.data, error: null };

export async function fetchMe(): Promise<Result<AccountMe>> {
  return unwrap(await apiFetch<AccountMe>('/api/account/me'));
}

export async function patchProfile(body: ProfilePatch): Promise<Result<AccountMe>> {
  return unwrap(await apiFetch<AccountMe>('/api/account/profile', { method: 'PATCH', body: JSON.stringify(body) }));
}

export async function patchEmail(new_email: string, current_password: string): Promise<Result<AccountMe>> {
  return unwrap(await apiFetch<AccountMe>('/api/account/email', { method: 'PATCH', body: JSON.stringify({ new_email, current_password }) }));
}

export async function postChangePassword(current_password: string, new_password: string): Promise<Result<{ ok: boolean }>> {
  return unwrap(await apiFetch<{ ok: boolean }>('/api/account/change-password', { method: 'POST', body: JSON.stringify({ current_password, new_password }) }));
}

// PR-000042: server-side check of the caller's own password (shared step-up machinery, no side
// effects, rate-limited). Used before re-wrapping the device-local PIN key on reset.
export async function verifyStepUpPassword(password: string): Promise<Result<{ verified: boolean }>> {
  return unwrap(await apiFetch<{ verified: boolean }>('/api/account/verify-step-up', { method: 'POST', body: JSON.stringify({ step_up: { password } }) }));
}
